<template>
    <div class="podcast-corporate">
        <!-- Generation state messages (shown to whoever is waiting on the podcast). -->
        <e-message v-if="showPendingOrGeneratingMessage" type="is-warning" :closable="false">
            This podcast is being generated and is not available yet. Please check back in a few minutes.
        </e-message>

        <e-message v-if="generationStatus === 'failed'" type="is-danger" :closable="false">
            {{ failedGenerationMessage }}
        </e-message>

        <e-message v-if="showCompletedWithoutAudioMessage" type="is-info" :closable="false">
            Podcast generation is complete, but the audio URL is not available yet. Please refresh in a moment.
        </e-message>

        <!-- Learner view (Figma 17111:48429): two stacked cards in the content column. -->
        <template v-if="isPodcastReady">
            <!-- Podcast Episode card: native audio bar + a Playback Speed pill (Figma 17111:48457/48475). -->
            <section class="podcast-card">
                <header class="podcast-card__header">Podcast Episode</header>
                <div class="podcast-card__body podcast-card__body--episode">
                    <!-- Hidden native engine. The custom controls below drive it; we keep
                         the element only as the media source + event source so the inherited
                         playback-progress sync (onTimeUpdate / onEnded) is unchanged. -->
                    <!-- eslint-disable-next-line vue-a11y/media-has-caption -->
                    <audio
                        ref="audioEl"
                        class="podcast-audio-engine"
                        :src="audioUrl"
                        preload="metadata"
                        @loadedmetadata="onNativeLoaded"
                        @timeupdate="onNativeTimeUpdate"
                        @ended="onNativeEnded"
                        @play="isPlaying = true"
                        @pause="isPlaying = false"
                        @volumechange="onNativeVolumeChange"
                    />

                    <!-- Player: PrimeVue Button (play/mute) + Slider (seek/volume), used
                         directly per docs/buefy-to-primevue-migration.md (E-components are
                         transitional). They inherit theme + dark mode + keyboard a11y. -->
                    <div class="podcast-player">
                        <PrimeVueButton
                            class="podcast-player__play"
                            rounded
                            :icon="isPlaying ? 'pi pi-pause' : 'pi pi-play'"
                            :aria-label="isPlaying ? 'Pause' : 'Play'"
                            @click="togglePlay"
                        />

                        <span class="podcast-player__time">
                            {{ formatTime(currentTime) }} / {{ formatTime(duration) }}
                        </span>

                        <Slider
                            class="podcast-player__seek"
                            :model-value="progressPercent"
                            :min="0"
                            :max="100"
                            :step="0.1"
                            aria-label="Seek"
                            @update:model-value="onScrubValue"
                        />

                        <div class="podcast-player__volume">
                            <PrimeVueButton
                                class="podcast-player__icon-btn"
                                text
                                rounded
                                severity="secondary"
                                :icon="volumeIcon"
                                :aria-label="isMuted ? 'Unmute' : 'Mute'"
                                @click="toggleMute"
                            />
                            <Slider
                                class="podcast-player__volume-slider"
                                :model-value="displayVolume"
                                :min="0"
                                :max="100"
                                aria-label="Volume"
                                @update:model-value="onVolumeValue"
                            />
                        </div>
                    </div>

                    <div class="podcast-speed">
                        <button
                            type="button"
                            class="podcast-speed__trigger"
                            :aria-expanded="speedMenuOpen"
                            @click.stop="speedMenuOpen = !speedMenuOpen"
                        >
                            <i class="pi pi-clock" aria-hidden="true" />
                            <span>Playback Speed</span>
                            <span class="podcast-speed__value">{{ playbackRate }}x</span>
                            <i class="pi pi-chevron-down" aria-hidden="true" />
                        </button>
                        <ul v-if="speedMenuOpen" class="podcast-speed__menu">
                            <li v-for="rate in playbackRates" :key="rate">
                                <button
                                    type="button"
                                    :class="{ 'is-active': rate === playbackRate }"
                                    @click="setPlaybackRate(rate)"
                                >
                                    {{ rate }}x
                                </button>
                            </li>
                        </ul>
                    </div>
                </div>
            </section>

            <!-- Transcript card (collapsible) -->
            <section v-if="transcriptScript" class="podcast-card">
                <button
                    type="button"
                    class="podcast-card__header podcast-card__header--toggle"
                    :aria-expanded="scriptAccordionOpen"
                    @click="scriptAccordionOpen = !scriptAccordionOpen"
                >
                    <span>Transcript</span>
                    <i
                        :class="scriptAccordionOpen ? 'pi pi-chevron-up' : 'pi pi-chevron-down'"
                        aria-hidden="true"
                    />
                </button>
                <div v-show="scriptAccordionOpen" class="podcast-card__body podcast-transcript">
                    {{ transcriptScript }}
                </div>
            </section>
        </template>

        <PrimeVueDialog
            v-model:visible="showRegenerateScriptConfirmDialog"
            header="Regenerate podcast audio?"
            modal
            :style="{ width: '460px' }"
            append-to="body"
        >
            <div class="tw:pl-2">
                <p>You changed the script. Regenerating will replace the current audio using the updated script.</p>
            </div>
            <template #footer>
                <PrimeVueButton label="Cancel" class="p-button-text" @click="showRegenerateScriptConfirmDialog = false" />
                <PrimeVueButton label="Regenerate with current script" class="p-button-primary" @click="queuePodcastGeneration" />
            </template>
        </PrimeVueDialog>

        <activity-completion-action-row class="podcast-corporate-action">
            <e-button
                v-if="isPodcastReady && (userHasAuthorizedAttempt || (!completed && !readOnly))"
                :loading="isLoading"
                :disabled="disabled"
                type="is-primary"
                @click="submit"
            >
                {{ translate('mark_complete') }}
            </e-button>
        </activity-completion-action-row>

        <!-- Instructor authoring (admins / course-editors). Sits BELOW the learner
             content so the player stays prominent. Behaviour inherited from Podcast.vue. -->
        <div v-if="canShowGeneratePodcast" class="podcast-authoring">
            <h2 class="podcast-authoring__heading">Podcast authoring</h2>

            <div class="tw:w-full podcast-config-panel">
                <podcast-configuration-summary
                    class="tw:mb-3"
                    :selected-count="selectedConfigCount"
                    :readonly="!canEditPodcastConfiguration"
                    @configure="isConfigDrawerOpen = true"
                />
                <e-field label="Delivery Instructions">
                    <e-input
                        :model-value="generationContext"
                        type="textarea"
                        rows="4"
                        class="tw:w-full"
                        :disabled="!canEditPodcastConfiguration"
                        @update:modelValue="generationContext = $event"
                    />
                </e-field>
                <e-field label="Script">
                    <e-input
                        :model-value="script"
                        type="textarea"
                        rows="10"
                        class="tw:w-full podcast-script-editor"
                        :disabled="!canEditPodcastConfiguration"
                        @update:modelValue="handleScriptInput"
                    />
                </e-field>
            </div>

            <e-field label="Podcast Generation">
                <div class="tw:mb-6">
                    <e-button type="is-primary" :loading="isGenerating" @click="generatePodcast">
                        {{ generationStatus === 'completed' ? 'Regenerate Podcast' : 'Generate Podcast' }}
                    </e-button>
                </div>
                <p class="tw:ml-4">
                    Status: <strong>{{ generationStatusLabel }}</strong>
                </p>
                <p v-if="generationError" class="tw:mt-1 tw:text-red-600">{{ generationError }}</p>
            </e-field>
        </div>

        <podcast-configuration-drawer
            :open="isConfigDrawerOpen"
            :readonly="!canEditPodcastConfiguration"
            :model-value="podcastConfig"
            @update:open="isConfigDrawerOpen = $event"
            @update:modelValue="podcastConfig = $event"
            @apply="handleConfigApply"
        />
    </div>
</template>

<script>
import PrimeVueButton from 'primevue/button';
import Slider from 'primevue/slider';
import { useI18n } from 'vue-i18n';

import Podcast from './Podcast.vue';

/**
 * Corporate presentation of the Podcast activity.
 *
 * Extends the stanbridge `Podcast.vue` so ALL behaviour (generation, audio-url
 * resolution, status polling, playback-progress sync, transcript handling, submit)
 * is inherited unchanged — only the template + styles are overridden to match the
 * corporate Figma learner view. The dispatcher selects this variant for the
 * corporate client via CORPORATE_RENDERER_OVERRIDES. Stanbridge is untouched.
 */
export default {
    name: 'PodcastCorporate',

    components: { PrimeVueButton, Slider },

    extends: Podcast,

    // `extends` merges Options (data/computed/methods/mixins) but NOT the parent's
    // setup() return, so re-provide translate here.
    setup() {
        const { t: translate } = useI18n();
        return { translate };
    },

    data: () => ({
        // Transcript collapsed by default in the corporate design.
        scriptAccordionOpen: false,
        speedMenuOpen: false,
        playbackRate: 1,
        playbackRates: [0.5, 0.75, 1, 1.25, 1.5, 2],
        // Custom-player UI state (mirrors the native <audio> element).
        isPlaying: false,
        currentTime: 0,
        duration: 0,
        volume: 100,
        isMuted: false,
    }),

    computed: {
        progressPercent() {
            return this.duration ? Math.min(100, (this.currentTime / this.duration) * 100) : 0;
        },

        displayVolume() {
            return this.isMuted ? 0 : this.volume;
        },

        volumeIcon() {
            if (this.isMuted || this.volume === 0) return 'pi pi-volume-off';
            return this.volume < 50 ? 'pi pi-volume-down' : 'pi pi-volume-up';
        },
    },

    mounted() {
        document.addEventListener('click', this.closeSpeedMenu);
    },

    beforeUnmount() {
        document.removeEventListener('click', this.closeSpeedMenu);
    },

    methods: {
        // Native <audio> drives the inherited playback-progress sync (onTimeUpdate /
        // onEnded from Podcast.vue), which can auto-complete the activity.
        onNativeLoaded() {
            const audio = this.$refs.audioEl;
            if (!audio) return;
            if (this.lastPosition > 0) {
                audio.currentTime = this.lastPosition;
            }
            audio.playbackRate = this.playbackRate;
            this.duration = audio.duration || 0;
            this.currentTime = audio.currentTime || 0;
            this.volume = Math.round(audio.volume * 100);
            this.isMuted = audio.muted;
        },

        onNativeTimeUpdate() {
            const audio = this.$refs.audioEl;
            if (!audio) return;
            this.currentTime = audio.currentTime || 0;
            if (audio.duration) this.duration = audio.duration;
            this.onTimeUpdate({
                currentTime: Math.floor(audio.currentTime || 0),
                duration: Math.floor(audio.duration || 0),
            });
        },

        onNativeEnded() {
            this.onEnded();
        },

        onNativeVolumeChange() {
            const audio = this.$refs.audioEl;
            if (!audio) return;
            this.volume = Math.round(audio.volume * 100);
            this.isMuted = audio.muted || audio.volume === 0;
        },

        togglePlay() {
            const audio = this.$refs.audioEl;
            if (!audio) return;
            if (audio.paused) {
                audio.play();
            } else {
                audio.pause();
            }
        },

        // Slider emits the new 0-100 value directly.
        onScrubValue(value) {
            const audio = this.$refs.audioEl;
            if (!audio || !this.duration) return;
            audio.currentTime = (Number(value) / 100) * this.duration;
            this.currentTime = audio.currentTime;
        },

        onVolumeValue(value) {
            const audio = this.$refs.audioEl;
            if (!audio) return;
            const v = Number(value);
            audio.volume = v / 100;
            audio.muted = v === 0;
        },

        toggleMute() {
            const audio = this.$refs.audioEl;
            if (!audio) return;
            audio.muted = !audio.muted;
        },

        formatTime(seconds) {
            const total = Math.max(0, Math.floor(seconds || 0));
            const mins = Math.floor(total / 60);
            const secs = total % 60;
            return `${mins}:${String(secs).padStart(2, '0')}`;
        },

        setPlaybackRate(rate) {
            this.playbackRate = rate;
            this.speedMenuOpen = false;
            const audio = this.$refs.audioEl;
            if (audio) {
                audio.playbackRate = rate;
            }
        },

        closeSpeedMenu(event) {
            if (this.speedMenuOpen && !event.target.closest('.podcast-speed')) {
                this.speedMenuOpen = false;
            }
        },
    },
};
</script>

<style scoped lang="scss">
.podcast-corporate {
    // The corporate shell section owns the 860px content measure; fill it.
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 18px;

    // Figma card: white, hairline border, soft shadow, rounded.
    .podcast-card {
        width: 100%;
        background: #fff;
        border: 1px solid #e5eff5;
        border-radius: 7.2px;
        box-shadow: 0 4px 4px rgba(2, 5, 10, 0.04);
    }

    .podcast-card__header {
        display: flex;
        align-items: center;
        padding: 18px 21.6px;
        border-bottom: 1px solid #e5eff5;
        color: #363636;
        font-weight: 700;
        font-size: 1.0125rem; // ~16.2px
    }

    .podcast-card__header--toggle {
        width: 100%;
        justify-content: space-between;
        background: none;
        cursor: pointer;
        text-align: left;

        i {
            color: #94a3b8;
        }
    }

    .podcast-card__body {
        padding: 19px 21.6px;
    }

    .podcast-card__body--episode {
        display: flex;
        flex-direction: column;
        gap: 18px;
    }

    // Hidden engine — custom controls drive it.
    .podcast-audio-engine {
        display: none;
    }

    // Player row: PrimeVue Button (play/mute) + Slider (seek/volume). PrimeVue
    // supplies the brand/theme colors; we only set sizing + layout here.
    .podcast-player {
        display: flex;
        align-items: center;
        gap: 18px;
    }

    // Round primary play button (PrimeVue Button).
    .podcast-player__play.p-button {
        flex: none;
        width: 48px;
        height: 48px;
        padding: 0;

        :deep(.p-button-icon) {
            font-size: 18px;
        }
    }

    .podcast-player__time {
        flex: none;
        min-width: 88px;
        font-variant-numeric: tabular-nums;
        font-size: 13px;
        color: var(--p-text-color);
    }

    .podcast-player__seek.p-slider {
        flex: 1;
    }

    .podcast-player__volume {
        flex: none;
        display: flex;
        align-items: center;
        gap: 8px;
    }

    .podcast-player__volume-slider.p-slider {
        width: 84px;
    }

    // Ghost mute button.
    .podcast-player__icon-btn.p-button {
        flex: none;
        width: 34px;
        height: 34px;
        padding: 0;
        color: var(--p-text-muted-color);

        :deep(.p-button-icon) {
            font-size: 18px;
        }
    }

    // Playback Speed pill (Figma secondary button), right-aligned below the player.
    .podcast-speed {
        position: relative;
        align-self: flex-end;
    }

    .podcast-speed__trigger {
        display: inline-flex;
        align-items: center;
        gap: 7px;
        padding: 8px 11.5px;
        border: 1px solid var(--p-content-border-color);
        border-radius: 6px;
        background: var(--p-content-background);
        color: var(--p-text-muted-color);
        font-size: 14px;
        font-weight: 500;
        cursor: pointer;

        .pi {
            font-size: 14px;
        }

        &:hover {
            background: color-mix(in srgb, var(--p-text-color) 6%, transparent);
        }
    }

    .podcast-speed__value {
        font-weight: 600;
        color: var(--p-text-color);
    }

    .podcast-speed__menu {
        position: absolute;
        right: 0;
        top: calc(100% + 4px);
        z-index: 10;
        margin: 0;
        padding: 4px;
        list-style: none;
        min-width: 7rem;
        background: var(--p-content-background);
        border: 1px solid var(--p-content-border-color);
        border-radius: 6px;
        box-shadow: 0 4px 12px rgba(2, 5, 10, 0.08);

        button {
            display: block;
            width: 100%;
            padding: 6px 10px;
            border: 0;
            border-radius: 4px;
            background: none;
            text-align: left;
            font-size: 14px;
            color: var(--p-text-muted-color);
            cursor: pointer;

            &:hover {
                background: color-mix(in srgb, var(--p-text-color) 6%, transparent);
            }

            &.is-active {
                color: var(--p-primary-color);
                font-weight: 600;
            }
        }
    }

    .podcast-transcript {
        max-height: 28rem;
        overflow-y: auto;
        white-space: pre-wrap;
        word-break: break-word;
        overflow-wrap: anywhere;
        color: #363636;
        font-size: 0.9rem;
        line-height: 1.5;
    }

    .podcast-corporate-action {
        margin-top: 0;
    }

    // Instructor authoring sits below the learner content, set off by a divider.
    .podcast-authoring {
        padding-top: 18px;
        border-top: 1px solid #e5eff5;
    }

    .podcast-authoring__heading {
        margin-bottom: 12px;
        color: #475569;
        font-size: 0.9rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.03em;
    }
}

// Instructor authoring panel (carried over from Podcast.vue).
.podcast-config-panel {
    width: 100%;
    max-width: 50vw;
}

@media (max-width: 768px) {
    .podcast-config-panel {
        max-width: 100%;
    }
}

.podcast-script-editor :deep(textarea) {
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace;
}
</style>
